import { useMemo } from 'react'
import { splitQuery } from '@/lib/search-utils'
import { OPPORTUNITY_TYPE_NAMES } from '../constants'
import type { NameResolutionMaps, SearchToken } from '../types'

/**
 * 検索クエリをトークンに分解するカスタムフック
 *
 * 検索文字列を解析し、タグ、取引先、ステータスなどの構造化されたトークンに変換します
 */
export function useSearchTokens(value: string, maps: NameResolutionMaps): SearchToken[] {
  return useMemo<SearchToken[]>(() => {
    if (!value) return []

    const parts = splitQuery(value)
    const result: SearchToken[] = []

    for (const part of parts) {
      // タグ
      if (part.startsWith('tag:')) {
        const segments = part.split(':')
        const groupOrName = segments[1] || ''
        const name = segments[2]
        if (name) {
          result.push({ type: 'tag', value: name, group: groupOrName, raw: part })
        } else if (groupOrName) {
          result.push({ type: 'tag', value: groupOrName, group: null, raw: part })
        }
        continue
      }

      // 除外タグ
      if (part.startsWith('exclude_tag:')) {
        const segments = part.split(':')
        const groupOrName = segments[1] || ''
        const name = segments[2]
        if (name) {
          result.push({ type: 'exclude_tag', value: name, group: groupOrName, raw: part })
        } else if (groupOrName) {
          result.push({ type: 'exclude_tag', value: groupOrName, group: null, raw: part })
        }
        continue
      }

      // 取引先
      if (part.startsWith('customer:')) {
        const id = Number.parseInt(part.split(':')[1] || '', 10)
        if (!Number.isNaN(id)) {
          result.push({ type: 'customer', id, name: maps.customerMap.get(id) || `ID:${id}`, raw: part })
        }
        continue
      }

      // 縦組織
      if (part.startsWith('vertical_division:')) {
        const id = Number.parseInt(part.split(':')[1] || '', 10)
        if (!Number.isNaN(id)) {
          result.push({
            type: 'vertical_division',
            id,
            name: maps.verticalDivisionMap.get(id) || `ID:${id}`,
            raw: part,
          })
        }
        continue
      }

      // 横組織
      if (part.startsWith('lateral_division:')) {
        const id = Number.parseInt(part.split(':')[1] || '', 10)
        if (!Number.isNaN(id)) {
          result.push({
            type: 'lateral_division',
            id,
            name: maps.lateralDivisionMap.get(id) || `ID:${id}`,
            raw: part,
          })
        }
        continue
      }

      // 起票者
      if (part.startsWith('created_by:')) {
        const id = Number.parseInt(part.split(':')[1] || '', 10)
        if (!Number.isNaN(id)) {
          result.push({ type: 'created_by', id, name: maps.captainMap.get(id) || `ID:${id}`, raw: part })
        }
        continue
      }

      // 売上計上グループ
      if (part.startsWith('sales_recording_group:')) {
        const id = Number.parseInt(part.split(':')[1] || '', 10)
        if (!Number.isNaN(id)) {
          result.push({
            type: 'sales_recording_group',
            id,
            name: maps.salesRecordingGroupMap.get(id) || `ID:${id}`,
            raw: part,
          })
        }
        continue
      }

      // ステータス
      if (part.startsWith('opportunity_status_id:')) {
        const id = Number.parseInt(part.split(':')[1] || '', 10)
        if (!Number.isNaN(id)) {
          result.push({
            type: 'opportunity_status_id',
            id,
            name: maps.statusMap.get(id) || `ID:${id}`,
            raw: part,
          })
        }
        continue
      }

      // オポ種別
      if (part.startsWith('opportunity_type:')) {
        const id = Number.parseInt(part.split(':')[1] || '', 10)
        if (!Number.isNaN(id)) {
          result.push({ type: 'opportunity_type', id, name: OPPORTUNITY_TYPE_NAMES[id] || `ID:${id}`, raw: part })
        }
        continue
      }

      // インセンティブフィルター
      if (part.startsWith('incentive_filter:')) {
        const filter = part.split(':')[1] as 'target' | 'disabled'
        if (filter === 'target' || filter === 'disabled') {
          result.push({ type: 'incentive_filter', filter, raw: part })
        }
        continue
      }

      // 除外キーワード
      if (part.startsWith('-')) {
        const keyword = part.substring(1).replace(/^["']|["']$/g, '')
        if (keyword) {
          result.push({ type: 'exclude', value: keyword, raw: part })
        }
        continue
      }

      // 通常のキーワード
      const keyword = part.replace(/^["']|["']$/g, '')
      if (keyword) {
        result.push({ type: 'query', value: keyword, raw: part })
      }
    }

    return result
  }, [value, maps])
}
